import { flow, Instance, types } from 'mobx-state-tree'
import { addEntityMaster } from '../../network/addEntityMaster'
import { deleteEntityMaster } from '../../network/deleteEntityMaster'
import { entityConfigApprovalActionApi } from '../../network/entityConfigApprovalActionApi'
import { entityProcessApprovals } from '../../network/entityProcessApprovals'
import { getAllEntityMaster, getAllEntityMasterRequest } from '../../network/getAllEntityMaster'
import { getEntityById } from '../../network/getEntityById'
import { getEntityConfigApprovals } from '../../network/getEntityConfigApprovals'
import { getEntityIdNameMap } from '../../network/getEntityIdNameMap'
import { syncPAM } from '../../network/syncPAM'
import { updateEntityMaster } from '../../network/updateEntityMaster'
import { getErrorMessage, MessageTypes } from '../../utils'
import { FORM_MESSAGE } from '../../utils/constants'
import { RootStore } from '../store'

const Contact = types.model('Contact', {
  name: types.maybeNull(types.string),
  number: types.maybeNull(types.string),
  email: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
})

const EndPoint = types.model('EndPoint', {
  domain: types.maybeNull(types.string),
  status: types.maybeNull(types.string),
  checksumEncryptionSelect: types.maybeNull(types.string),
  encryptionType: types.maybeNull(types.string),
  redirectUrl: types.maybeNull(types.string),
  enrollUrl: types.maybeNull(types.string),
  authUrl: types.maybeNull(types.string),
  stqUrl: types.maybeNull(types.string),
  s2sUrl: types.maybeNull(types.string),
  rpslS2sUrl: types.maybeNull(types.string),
})

const Entity = types.model('Entity', {
  id: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  status: types.maybeNull(types.union(types.boolean, types.string)),
  supportNetBanking: types.maybeNull(types.string),
  supportCard: types.maybeNull(types.string),
  contacts: types.maybeNull(types.array(Contact)),
  publicIps: types.maybeNull(types.array(types.string)),
  endpointInfo: types.maybeNull(EndPoint),
  updatedBy: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.string), //TODO: check with date
  updatedDate: types.maybeNull(types.string), //
})

export const EntityIdName = types.model('EntityIdName', {
  id: types.string,
  name: types.string,
})

const EntityMaster = types
  .model('EntityMaster', {
    entity: types.optional(Entity, {}),
    entities: types.array(Entity),
    allEntities: types.array(Entity), //NON PAGINATED COMPLETE RESP
    entityIdNames: types.array(EntityIdName),
  })
  .actions((self) => ({
    getEntity: flow(function* getEntity(id: number) {
      try {
        const response = yield getEntityById(id, RootStore.session.getTokens)
        const own = self
        own.entity = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    getAllEntities: flow(function* getAllEntities(data: any, paginationInfo: any, storeResponse?: boolean) {
      try {
        if (!storeResponse || self.allEntities?.length === 0) {
          const request = getAllEntityMasterRequest(data, paginationInfo)
          const response = yield getAllEntityMaster(request, RootStore.session.getTokens)
          if (storeResponse) {
            self.allEntities = response.content
          }
          return response
        }
        return { content: self.allEntities }
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    addEntity: flow(function* addEntity(data: any) {
      try {
        const response = yield addEntityMaster(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Added entity', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    deleteEntity: flow(function* deleteEntity(id) {
      try {
        const response = yield deleteEntityMaster(id, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Added entity', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),

    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield entityProcessApprovals(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Added entity', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    updateEntity: flow(function* updateEntity(data: any) {
      try {
        const response = yield updateEntityMaster(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Added entity', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),
    getEntityNameMap: flow(function* getEntityNameMap() {
      try {
        const response = yield getEntityIdNameMap(RootStore.session.getTokens)
        const own = self
        own.entityIdNames = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    syncPam: flow(function* syncPam() {
      try {
        const response = yield syncPAM(RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message ?? 'Sync PAM Success', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getEntityConfigApprovalsAsync: flow(function* getEntityConfigApprovalsAsync(reqObj: any) {
      try {
        reqObj.workflowStateList = ['OPS_CHECKER']
        const response = yield getEntityConfigApprovals(reqObj)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    entityConfigApprovalActionAsync: flow(function* entityConfigApprovalActionAsync(
      taskInstanceId: any,
      approvalAction,
    ) {
      try {
        const response = yield entityConfigApprovalActionApi(taskInstanceId, approvalAction)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
  }))
  .views((self) => ({
    getEntityView() {
      return {
        info: {
          id: self.entity.id,
          name: self.entity.name,
          type: self.entity.type,
          status: self.entity.status ? 'Enabled' : 'Disabled',
          supportNetBanking: self.entity.supportNetBanking,
          supportCard: self.entity.supportCard,
        },
        endpoint: {
          domain: self.entity.endpointInfo?.domain,
          domainStatus: self.entity.endpointInfo?.status,
          'checksum/Encryption': self.entity.endpointInfo?.checksumEncryptionSelect,
          encryptionType: self.entity.endpointInfo?.encryptionType,
          redirectUrl: self.entity.endpointInfo?.redirectUrl,
          enrollUrl: self.entity.endpointInfo?.enrollUrl,
          authUrl: self.entity.endpointInfo?.authUrl,
          stqUrl: self.entity.endpointInfo?.stqUrl,
          s2sUrl: self.entity.endpointInfo?.s2sUrl,
          rpslS2sUrl: self.entity.endpointInfo?.rpslS2sUrl,
        },
        contactInfo: self.entity.contacts,
        ipDetails: self.entity.publicIps?.map((item, index) => ({
          id: index,
          publicIp: item,
        })),
      }
    },
    getEditEntityView() {
      return {
        info: {
          name: self.entity.name,
          type: self.entity.type,
          status: self.entity.status ? 'Enable' : 'Disable',
          supportNetBanking: self.entity.supportNetBanking,
          supportCard: self.entity.supportCard,
        },
        endpoint: {
          endpointDomain: self.entity.endpointInfo?.domain,
          endpointStatus: self.entity.endpointInfo?.status,
          checksumEncryptionSelect: self.entity.endpointInfo?.checksumEncryptionSelect,
          encryptionType: self.entity.endpointInfo?.encryptionType,
          entityRedirectURL: self.entity.endpointInfo?.redirectUrl,
          entityEnrollURL: self.entity.endpointInfo?.enrollUrl,
          entityAuthURL: self.entity.endpointInfo?.authUrl,
          entityStqURL: self.entity.endpointInfo?.stqUrl,
          entityS2SURL: self.entity.endpointInfo?.s2sUrl,
          rpslS2SURL: self.entity.endpointInfo?.rpslS2sUrl,
        },
        contactPDetails: self.entity.contacts,
        publicIp: self.entity.publicIps,
      }
    },
    getEntityList() {
      const list = self.entities?.map((item) => {
        return {
          ID: '' + item?.id,
          entityId: '' + item?.id,
          name: item?.name,
          type: item?.type,
          status: item?.status ? 'ENABLED' : 'DISABLED',
          supportNetBanking: item?.supportNetBanking,
          supportCard: item?.supportCard,
        }
      })
      return list
    },
    getFilteredEntityList(list: any, keys?: any, searchText?: any) {
      const entities = list ?? self?.entities ?? []
      const data = entities?.map?.((item: any) => {
        return {
          ID: '' + item?.id,
          entityId: '' + item?.id,
          name: item?.name,
          type: item?.type,
          status: item?.status ? 'ENABLED' : 'DISABLED',
          supportNetBanking: item?.supportNetBanking,
          supportCard: item?.supportCard,
          id: item.id,
        }
      })
      if (keys?.length > 0) {
        if (data.length > 0) {
          if (keys && keys?.length > 0) {
            const retArr: any = []
            data.map((item: any) => {
              let isMatching = true
              keys.map((obj: any) => {
                const val = isNaN(item[obj?.key]) ? item[obj?.key].toLowerCase() : item[obj?.key]?.toString()
                isMatching = isMatching && val.includes(obj?.searchString.toLowerCase())
              })
              isMatching && retArr.push(item)
              return retArr
            })
            return retArr
          } else {
            return list?.filter((obj: any) =>
              Object.keys(obj).some((key) => obj[key].toLowerCase().includes(searchText.toLowerCase())),
            )
          }
        }
      }
      return data
    },
    getEntityMap() {
      return self.entityIdNames.map((item) => {
        return {
          id: item.id,
          value: `${item.id}-${item.name}`,
        }
      })
    },
  }))

const entityMasterData = EntityMaster.create({
  entity: {},
  entities: [],
})

export type EntityMasterType = Instance<typeof EntityMaster>
export { Entity, EntityMaster, entityMasterData }
